// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, ChevronRight, ExternalLink, GripVertical,
  Loader2, Plus, RotateCcw, Search,
} from "lucide-react";
import { toast } from "sonner";
import { RichTextEditor } from "@/design-system/forms/RichTextEditor";
import type { EventData, FaqCatalogItem, EventFaqLink } from "@/types/events";

interface Props {
  event: EventData;
  eventId?: string;
}

export function FaqsSection({ event, eventId }: Props) {
  const [catalog, setCatalog] = useState<FaqCatalogItem[]>([]);
  const [links, setLinks] = useState<Record<string, EventFaqLink>>({}); // by faq_id
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [order, setOrder] = useState<string[]>([]); // faq ids in current display order

  // Inline create form
  const [showCreate, setShowCreate] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  const [newScope, setNewScope] = useState<"org" | "global">("org");
  const [creating, setCreating] = useState(false);

  const dragId = useRef<string | null>(null);

  const orgId = (event as any).organization_id;
  const sportId = event.sport_id;

  const load = async () => {
    setLoading(true);
    // Catalog filter
    let cq: any = (supabase as any)
      .from("faq_catalog")
      .select("*")
      .eq("is_active", true);
    if (orgId) cq = cq.or(`organization_id.eq.${orgId},organization_id.is.null`);
    if (sportId) cq = cq.or(`sport_id.eq.${sportId},sport_id.is.null`);
    cq = cq.order("display_order", { ascending: true });
    const { data: cat, error: cErr } = await cq;
    if (cErr) {
      toast.error(cErr.message);
      setLoading(false);
      return;
    }
    setCatalog(cat ?? []);

    // Existing links
    if (eventId) {
      const { data: lks } = await (supabase as any)
        .from("event_faq_links")
        .select("*")
        .eq("event_id", eventId);
      const map: Record<string, EventFaqLink> = {};
      (lks ?? []).forEach((l: any) => { map[l.faq_id] = l; });
      setLinks(map);

      // Order: linked-active first by display_order, then unlinked
      const activeLinked = (lks ?? [])
        .filter((l: any) => l.is_active)
        .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((l: any) => l.faq_id);
      const rest = (cat ?? [])
        .map((c: any) => c.id)
        .filter((id: string) => !activeLinked.includes(id));
      setOrder([...activeLinked, ...rest]);
    } else {
      setOrder((cat ?? []).map((c: any) => c.id));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, orgId, sportId]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    catalog.forEach((c) => c.category && set.add(c.category));
    return Array.from(set).sort();
  }, [catalog]);

  const visible = useMemo(() => {
    const byId: Record<string, FaqCatalogItem> = {};
    catalog.forEach((c) => { byId[c.id] = c; });
    return order
      .map((id) => byId[id])
      .filter(Boolean)
      .filter((c) => {
        if (filterCategory !== "all" && c.category !== filterCategory) return false;
        if (search.trim()) {
          const q = search.toLowerCase();
          if (!c.question.toLowerCase().includes(q) && !c.answer.toLowerCase().includes(q)) return false;
        }
        return true;
      });
  }, [catalog, order, filterCategory, search]);

  const isLinked = (faqId: string) => !!links[faqId]?.is_active;

  const toggleLink = async (faq: FaqCatalogItem, checked: boolean) => {
    if (!eventId) return toast.error("Save the event first");
    const existing = links[faq.id];
    if (checked) {
      const payload: any = {
        event_id: eventId,
        faq_id: faq.id,
        is_active: true,
        display_order: existing?.display_order ?? Object.values(links).filter((l: any) => l.is_active).length,
        ...(existing?.override_answer !== undefined ? { override_answer: existing.override_answer } : {}),
      };
      const { data, error } = await (supabase as any)
        .from("event_faq_links")
        .upsert(payload, { onConflict: "event_id,faq_id" })
        .select()
        .single();
      if (error) return toast.error(error.message);
      setLinks((p) => ({ ...p, [faq.id]: data }));
    } else {
      if (!existing) return;
      const { data, error } = await (supabase as any)
        .from("event_faq_links")
        .update({ is_active: false })
        .eq("id", existing.id)
        .select()
        .single();
      if (error) return toast.error(error.message);
      setLinks((p) => ({ ...p, [faq.id]: data }));
    }
  };

  const saveOverride = async (faq: FaqCatalogItem, html: string) => {
    if (!eventId) return;
    const existing = links[faq.id];
    const value = html === "" ? null : html;
    const payload: any = {
      event_id: eventId,
      faq_id: faq.id,
      override_answer: value,
      is_active: existing?.is_active ?? true,
      display_order: existing?.display_order ?? 0,
    };
    const { data, error } = await (supabase as any)
      .from("event_faq_links")
      .upsert(payload, { onConflict: "event_id,faq_id" })
      .select()
      .single();
    if (error) return toast.error(error.message);
    setLinks((p) => ({ ...p, [faq.id]: data }));
  };

  const resetOverride = (faq: FaqCatalogItem) => saveOverride(faq, "");

  const createCatalogFaq = async () => {
    const q = newQuestion.trim();
    const a = (newAnswer || "").trim();
    if (!q) return toast.error("Question is required");
    if (!a) return toast.error("Answer is required");
    setCreating(true);
    const payload: any = {
      question: q,
      answer: a,
      category: (newCategory || "general").trim() || "general",
      is_active: true,
      display_order: (catalog.length || 0),
      organization_id: newScope === "org" ? (orgId ?? null) : null,
      sport_id: sportId ?? null,
    };
    const { data: faq, error } = await (supabase as any)
      .from("faq_catalog")
      .insert(payload)
      .select()
      .single();
    if (error) {
      setCreating(false);
      return toast.error(error.message);
    }
    // Auto-link to this event if saved
    if (eventId) {
      const linkRow: any = {
        event_id: eventId,
        faq_id: faq.id,
        is_active: true,
        display_order: Object.values(links).filter((l: any) => l.is_active).length,
      };
      const { data: link, error: lerr } = await (supabase as any)
        .from("event_faq_links")
        .upsert(linkRow, { onConflict: "event_id,faq_id" })
        .select()
        .single();
      if (lerr) toast.error(lerr.message);
      else setLinks((p) => ({ ...p, [faq.id]: link }));
    }
    setCatalog((p) => [...p, faq]);
    setOrder((p) => [faq.id, ...p.filter((id) => id !== faq.id)]);
    setNewQuestion("");
    setNewAnswer("");
    setNewCategory("general");
    setShowCreate(false);
    setCreating(false);
    toast.success(eventId ? "FAQ added to catalog and linked" : "FAQ added to catalog");
  };


  const applyOrgDefaults = async () => {
    if (!eventId) return toast.error("Save the event first");
    const rows = catalog.map((c, idx) => ({
      event_id: eventId,
      faq_id: c.id,
      is_active: true,
      display_order: idx,
    }));
    if (!rows.length) return;
    const { error } = await (supabase as any)
      .from("event_faq_links")
      .upsert(rows, { onConflict: "event_id,faq_id" });
    if (error) return toast.error(error.message);
    toast.success(`Linked ${rows.length} catalog FAQ(s)`);
    load();
  };

  const selectAll = async (checked: boolean) => {
    if (!eventId) return toast.error("Save the event first");
    const targetIds = visible.map((c) => c.id);
    const rows = targetIds.map((faqId, idx) => ({
      event_id: eventId,
      faq_id: faqId,
      is_active: checked,
      display_order: idx,
    }));
    const { error } = await (supabase as any)
      .from("event_faq_links")
      .upsert(rows, { onConflict: "event_id,faq_id" });
    if (error) return toast.error(error.message);
    load();
  };

  // Drag reorder (HTML5)
  const onDragStart = (id: string) => { dragId.current = id; };
  const onDragOver = (e: React.DragEvent) => e.preventDefault();
  const onDrop = async (targetId: string) => {
    const src = dragId.current;
    dragId.current = null;
    if (!src || src === targetId) return;
    const next = [...order];
    const from = next.indexOf(src);
    const to = next.indexOf(targetId);
    if (from < 0 || to < 0) return;
    next.splice(to, 0, next.splice(from, 1)[0]);
    setOrder(next);
    if (!eventId) return;
    // Persist display_order for linked-active rows only
    const rows = next
      .filter((id) => links[id]?.is_active)
      .map((id, idx) => ({
        event_id: eventId,
        faq_id: id,
        is_active: true,
        display_order: idx,
        ...(links[id]?.override_answer !== undefined ? { override_answer: links[id].override_answer } : {}),
      }));
    if (!rows.length) return;
    const { error } = await (supabase as any)
      .from("event_faq_links")
      .upsert(rows, { onConflict: "event_id,faq_id" });
    if (error) toast.error(error.message);
    else load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Pick which FAQs to display on this event's public page. Manage the master list in the FAQs catalog.
        </p>
        <Link
          to="/admin-dashboard/faqs"
          className="text-sm text-primary hover:underline flex items-center gap-1 flex-shrink-0"
        >
          Manage FAQ catalog <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {/* Inline create FAQ */}
      <div className="border border-border rounded-lg bg-card">
        {!showCreate ? (
          <button
            onClick={() => setShowCreate(true)}
            className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-primary hover:bg-secondary/40 rounded-lg"
          >
            <Plus className="w-4 h-4" /> Add a new FAQ to the catalog
          </button>
        ) : (
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">New FAQ</h4>
              <button
                onClick={() => setShowCreate(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Question</label>
              <Input
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="e.g. What is the refund policy?"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Answer</label>
              <RichTextEditor value={newAnswer} onChange={setNewAnswer} minHeight="120px" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Category</label>
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="general"
                  list="faq-category-suggestions"
                />
                <datalist id="faq-category-suggestions">
                  {categories.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Visibility</label>
                <select
                  value={newScope}
                  onChange={(e) => setNewScope(e.target.value as any)}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="org">This organization only</option>
                  <option value="global">Global (all organizations)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={createCatalogFaq} disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add FAQ"}
              </Button>
            </div>
            {!eventId && (
              <p className="text-xs text-muted-foreground">
                Save the event first to auto-link this FAQ. It will still be added to the catalog.
              </p>
            )}
          </div>
        )}
      </div>

      {catalog.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-10 text-center">
          <p className="text-sm text-muted-foreground mb-4">No FAQs in the catalog yet.</p>
          <Link to="/admin-dashboard/faqs">
            <Button>
              <Plus className="w-4 h-4 mr-1" /> Create FAQ
            </Button>
          </Link>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFilterCategory("all")}
              className={`px-3 py-1 rounded-full text-xs font-medium border ${filterCategory === "all" ? "bg-primary text-white border-primary" : "bg-background text-foreground border-border hover:bg-secondary"}`}
            >
              All categories
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilterCategory(c)}
                className={`px-3 py-1 rounded-full text-xs font-medium border ${filterCategory === c ? "bg-primary text-white border-primary" : "bg-background text-foreground border-border hover:bg-secondary"}`}
              >
                {c}
              </button>
            ))}
            <div className="relative ml-auto w-56">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="pl-8 h-8"
              />
            </div>
          </div>

          {/* Bulk actions */}
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => selectAll(true)} className="text-primary hover:underline">Select all</button>
            <span className="text-muted-foreground">•</span>
            <button onClick={() => selectAll(false)} className="text-primary hover:underline">Deselect all</button>
            <span className="text-muted-foreground">•</span>
            <button onClick={applyOrgDefaults} className="text-primary hover:underline">Apply org defaults</button>
            <span className="ml-auto text-xs text-muted-foreground">
              {Object.values(links).filter((l: any) => l.is_active).length} linked
            </span>
          </div>

          {/* List */}
          <div className="space-y-2">
            {visible.map((c) => {
              const linked = isLinked(c.id);
              const link = links[c.id];
              const hasOverride = !!link?.override_answer;
              const isOpen = !!expanded[c.id];
              return (
                <div
                  key={c.id}
                  draggable={linked}
                  onDragStart={() => onDragStart(c.id)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(c.id)}
                  className={`border border-border rounded-lg p-3 bg-card transition-opacity ${linked ? "" : "opacity-60"}`}
                >
                  <div className="flex items-start gap-3">
                    {linked ? (
                      <GripVertical className="w-4 h-4 mt-1 text-muted-foreground cursor-grab" />
                    ) : (
                      <span className="w-4" />
                    )}
                    <Checkbox
                      checked={linked}
                      onCheckedChange={(v) => toggleLink(c, !!v)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">{c.question}</span>
                        <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border bg-secondary text-foreground border-border">
                          {c.category || "general"}
                        </span>
                        {hasOverride && (
                          <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border bg-amber-100 text-amber-700 border-amber-200">
                            Custom answer
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        {(link?.override_answer || c.answer || "").replace(/<[^>]+>/g, "").slice(0, 140)}
                      </p>

                      {linked && (
                        <button
                          onClick={() => setExpanded((p) => ({ ...p, [c.id]: !isOpen }))}
                          className="mt-2 text-xs text-primary hover:underline inline-flex items-center gap-1"
                        >
                          {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          Override answer for this event
                        </button>
                      )}

                      {linked && isOpen && (
                        <div className="mt-2 space-y-2">
                          <RichTextEditor
                            value={link?.override_answer ?? c.answer ?? ""}
                            onChange={(html) => {
                              // Optimistic local update; debounce-save on blur via timeout
                              setLinks((p) => ({
                                ...p,
                                [c.id]: { ...(p[c.id] ?? {}), override_answer: html } as any,
                              }));
                            }}
                            minHeight="120px"
                          />
                          <div className="flex items-center gap-3 text-xs">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => saveOverride(c, links[c.id]?.override_answer ?? "")}
                            >
                              Save override
                            </Button>
                            {hasOverride && (
                              <button
                                onClick={() => resetOverride(c)}
                                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                              >
                                <RotateCcw className="w-3 h-3" /> Reset to catalog answer
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
